import {StatusType} from '@datadog/browser-logs';
import {init} from '@grammarly/editor-sdk-react';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {textToHtml} from '../../../srcCommon/services/converters/textToHtmlConverter';
import {logger} from '../../../srcCommon/services/logger/logger';
import {LogMessageContext} from '../../../srcCommon/services/logger/loggerCommon';
import {noopPersistedStateProvider} from '../../../srcCommon/services/persistentState/noopPersistedStateProvider';
import {getGrammarlyClientId} from '../../components/grammarly/grammarlyConstants';
import {getCompanyHasGrammarlyPremium} from '../../components/grammarly/grammarlySelectors';
import {ThunkAction, ThunkAsyncAction} from '../../store/appThunk';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {signoutUserAsyncAction} from '../../store/data/teammates/teammatesActions';
import {openHelpChatAction} from '../../store/panels/app/appPanelActions';
import {pushContentTypeAction} from '../../store/panels/app/appPanelRouteActions';
import {AppContentTypesEnum} from '../../store/panels/app/parameters/appParameters';
import {popoutEmlAsyncAction} from '../../store/panels/eml/emlPanelActions';
import {EmlSourceTypesEnum} from '../../store/panels/eml/emlPanelState';
import {getFirstAppPanelId} from '../../store/panels/panelsSelectors';
import {DownloadStatusesEnum, onElectronDownloadEvent} from '../browser';
import {getViewerConversation} from '../legacy/legacySelectors';
import {openConversationPrintPopup} from '../popups';
import {trackEvent, TrackingTypesEnum} from '../tracking';
import {tryNavigateToLinkAsync} from './electronBridgeNavigateToLink';

/*
 * Navigation.
 */

export function historyBackElectronAction() {
  globalWindow.history.back();
}

export function historyForwardElectronAction() {
  globalWindow.history.forward();
}

export function openHelpElectronAction(): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = getFirstAppPanelId(getState());

    dispatch(openHelpChatAction(appPanelId));
  };
}

export function openSettingsElectronAction(): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = getFirstAppPanelId(getState());

    dispatch(pushContentTypeAction(appPanelId, AppContentTypesEnum.SETTINGS));
  };
}

export function openUrlElectronAction(url: string): ThunkAction {
  return () => tryNavigateToLinkAsync(url);
}

/*
 * Downloads.
 */

export function downloadDidCompleteElectronAction(url: string, filepath: string) {
  onElectronDownloadEvent(url, {
    status: DownloadStatusesEnum.SUCCEEDED,
    filepath,
  });
}

export function downloadDidFailElectronAction(url: string) {
  onElectronDownloadEvent(url, {
    status: DownloadStatusesEnum.FAILED,
  });
}

export function downloadDidProgressElectronAction(url: string, progress: number) {
  onElectronDownloadEvent(url, {
    status: DownloadStatusesEnum.IN_PROGRESS,
    progress: progress / 100,
  });
}

/*
 * Miscellaneous.
 */

export function postMessageElectronAction(origin: string, data: unknown) {
  const event = new MessageEvent('message', {origin, data});
  globalWindow.dispatchEvent(event);
}

export function printElectronAction(): ThunkAction {
  return (_dispatch, getState) => {
    const viewerConversation = getViewerConversation(getState());
    if (!viewerConversation) {
      return;
    }

    openConversationPrintPopup(viewerConversation);
  };
}

export function showUpdateBannerElectronAction() {
  if (showUpdateBanner) {
    showUpdateBanner();
  }
}

export function signoutElectronAction() {
  return signoutUserAsyncAction(() => noopPersistedStateProvider.tryClearPersistedStateAsync());
}

export function trackEventElectronAction(name: string, value: unknown) {
  trackEvent(TrackingTypesEnum.ELECTRON, name, value);
}

export function openEmlFileElectronAsyncAction(emlFileContents: string): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = getFirstAppPanelId(getState());

    dispatch(
      popoutEmlAsyncAction(appPanelId, {
        type: EmlSourceTypesEnum.FILE,
        emlFileContents,
      }),
    );
  };
}

const electronLogger = logger.getLogger('electron');

export function datadogLogAction(message: string, status: StatusType, context?: LogMessageContext) {
  electronLogger[status].call(electronLogger, message, context);
}

export function grammarlyAuthAction(url: string): ThunkAction {
  return (_dispatch, getState) => {
    const hasGrammarlyPremium = getCompanyHasGrammarlyPremium(getState());
    init(getGrammarlyClientId(hasGrammarlyPremium)).then((grammarly) => {
      grammarly.handleOAuthCallback(url);
    });
  };
}

export function handleSharedContentElectronAction(
  sharedTextContents: ReadonlyArray<string>,
  hasAttachments: boolean,
): ThunkAsyncAction {
  return async (dispatch) => {
    const text = sharedTextContents.length > 0 ? sharedTextContents.join('\n\n') : undefined;
    const trustedHtml = text && textToHtml(text);
    const sendOptions = {text, trustedHtml, shouldAttachExternalResources: hasAttachments};
    dispatch(composeMessageAction(sendOptions));
  };
}

/*
 * Global registration.
 */

let showUpdateBanner: () => void | undefined;

// TODO: Use the state instead of a global registration mechanism.
export function registerShowUpdateBannerFunction(value: () => void) {
  showUpdateBanner = value;
}
